import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Globe,
  MapPin,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Server,
  Wifi,
  AlertTriangle,
  Eye,
  EyeOff,
  Cloud,
  Building2,
  Clock,
  Activity,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { ScoreBar } from "../ModuleScore";
import { IssuesList, type Issue } from "../IssuesList";
import { Badge } from "../../badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";

// IP Intelligence Module Data Types
interface IpGeolocation {
  continent_code?: string;
  continent_name?: string;
  country_code?: string;
  country_name?: string;
  region_code?: string;
  region_name?: string;
  city?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
  accuracy_radius?: number;
}

interface IpSecurityInfo {
  is_proxy?: boolean;
  proxy_type?: string;
  is_vpn?: boolean;
  is_tor?: boolean;
  is_relay?: boolean;
  is_crawler?: boolean;
  crawler_name?: string;
  crawler_type?: string;
  is_datacenter?: boolean;
  threat_level?: "unknown" | "low" | "medium" | "high" | "critical";
  threat_types?: string[];
  blocklist_count?: number;
}

interface IpNetworkInfo {
  isp?: string;
  organization?: string;
  asn?: number;
  as_name?: string;
  connection_type?: string;
  cloud_provider?: string;
  cloud_service?: string;
  cloud_region?: string;
}

interface IpMetadata {
  timezone_id?: string;
  timezone_offset?: string;
  currency_code?: string;
  currency_symbol?: string;
  languages?: string[];
  calling_code?: string;
  flag_emoji?: string;
  is_eu?: boolean;
}

interface RiskFactor {
  category: string;
  description: string;
  score_impact: number;
  severity: "info" | "low" | "medium" | "high" | "critical";
}

export interface IpIntelModuleData {
  ip: string;
  ip_version?: number;
  hostname?: string;
  geolocation?: IpGeolocation;
  security?: IpSecurityInfo;
  network?: IpNetworkInfo;
  metadata?: IpMetadata;
  score?: number;
  risk_factors?: RiskFactor[];
  lookup_time_ms?: number;
  cached?: boolean;
  provider?: string;
}

export interface IpIntelModuleViewProps {
  data: IpIntelModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Threat level colors
const THREAT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  unknown: { bg: "bg-slate-500/10", text: "text-slate-400", border: "border-slate-500/30" },
  low: { bg: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-500/30" },
  medium: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30" },
  high: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30" },
  critical: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30" },
};

// Get grade from score (inverted - lower is better for risk)
function getGrade(score?: number): string {
  if (score === undefined) return "?";
  if (score <= 10) return "A+";
  if (score <= 20) return "A";
  if (score <= 30) return "A-";
  if (score <= 40) return "B+";
  if (score <= 50) return "B";
  if (score <= 60) return "B-";
  if (score <= 70) return "C+";
  if (score <= 80) return "C";
  if (score <= 90) return "D";
  return "F";
}

// Location Card
function LocationCard({ geolocation, metadata }: { geolocation?: IpGeolocation; metadata?: IpMetadata }) {
  if (!geolocation) {
    return (
      <Card className="bg-card/50">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Location</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Unknown</p>
        </CardContent>
      </Card>
    );
  }

  const locationParts = [geolocation.city, geolocation.region_name, geolocation.country_name].filter(Boolean);
  const locationString = locationParts.length > 0 ? locationParts.join(", ") : "Unknown";

  return (
    <Card className="bg-card/50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Location</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          {metadata?.flag_emoji && <span className="text-2xl">{metadata.flag_emoji}</span>}
          <div>
            <p className="font-semibold">{locationString}</p>
            {geolocation.country_code && (
              <p className="text-xs text-muted-foreground">
                {geolocation.continent_name} ({geolocation.country_code})
              </p>
            )}
          </div>
        </div>

        {geolocation.latitude !== undefined && geolocation.longitude !== undefined && (
          <p className="text-xs font-mono text-muted-foreground">
            {geolocation.latitude.toFixed(4)}, {geolocation.longitude.toFixed(4)}
            {geolocation.accuracy_radius && ` (±${geolocation.accuracy_radius}km)`}
          </p>
        )}

        {metadata?.timezone_id && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {metadata.timezone_id} ({metadata.timezone_offset})
          </div>
        )}

        {metadata?.is_eu && (
          <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
            EU Member
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}

// Security Status Card
function SecurityStatusCard({ security }: { security?: IpSecurityInfo }) {
  if (!security) {
    return (
      <Card className="bg-card/50">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Security</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No security data</p>
        </CardContent>
      </Card>
    );
  }

  const threatLevel = security.threat_level || "unknown";
  const colors = THREAT_COLORS[threatLevel] || THREAT_COLORS.unknown;

  const isAnonymous = security.is_vpn || security.is_proxy || security.is_tor || security.is_relay;

  return (
    <Card className={cn("bg-card/50", colors.border, "border")}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {threatLevel === "critical" || threatLevel === "high" ? (
              <ShieldAlert className={cn("h-4 w-4", colors.text)} />
            ) : threatLevel === "low" || threatLevel === "unknown" ? (
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
            ) : (
              <Shield className={cn("h-4 w-4", colors.text)} />
            )}
            <CardTitle className="text-sm font-medium">Security Status</CardTitle>
          </div>
          <Badge variant="outline" className={cn("text-xs capitalize", colors.bg, colors.text, colors.border)}>
            {threatLevel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Anonymization Indicators */}
        <div className="grid grid-cols-2 gap-2">
          <AnonymizationBadge label="VPN" active={security.is_vpn} icon={Eye} />
          <AnonymizationBadge label="Proxy" active={security.is_proxy} icon={Server} type={security.proxy_type} />
          <AnonymizationBadge label="Tor" active={security.is_tor} icon={Globe} />
          <AnonymizationBadge label="Relay" active={security.is_relay} icon={Wifi} />
        </div>

        {/* Other indicators */}
        <div className="flex flex-wrap gap-2">
          {security.is_datacenter && (
            <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
              <Building2 className="h-3 w-3 mr-1" />
              Datacenter
            </Badge>
          )}
          {security.is_crawler && (
            <Badge variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
              <Activity className="h-3 w-3 mr-1" />
              {security.crawler_name || "Crawler"}
            </Badge>
          )}
          {security.blocklist_count !== undefined && security.blocklist_count > 0 && (
            <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {security.blocklist_count} blocklist{security.blocklist_count > 1 ? "s" : ""}
            </Badge>
          )}
        </div>

        {/* Threat Types */}
        {security.threat_types && security.threat_types.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">Threat Types</p>
            <div className="flex flex-wrap gap-1">
              {security.threat_types.map((type) => (
                <Badge
                  key={type}
                  variant="outline"
                  className="text-xs bg-red-500/10 text-red-400 border-red-500/30 capitalize"
                >
                  {type.replace(/_/g, " ")}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {!isAnonymous && !security.is_datacenter && security.threat_types?.length === 0 && (
          <div className="flex items-center gap-2 text-emerald-400">
            <ShieldCheck className="h-4 w-4" />
            <span className="text-sm">Clean IP - No threats detected</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Anonymization Badge
function AnonymizationBadge({
  label,
  active,
  icon: Icon,
  type,
}: {
  label: string;
  active?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  type?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 p-2 rounded-lg border text-xs",
        active
          ? "border-amber-500/30 bg-amber-500/10 text-amber-400"
          : "border-border bg-muted/30 text-muted-foreground"
      )}
    >
      <Icon className="h-3 w-3" />
      <span>{label}</span>
      {active && type && <span className="text-[10px] opacity-75">({type})</span>}
      {active ? <Eye className="h-3 w-3 ml-auto" /> : <EyeOff className="h-3 w-3 ml-auto opacity-50" />}
    </div>
  );
}

// Network Info Card
function NetworkInfoCard({ network }: { network?: IpNetworkInfo }) {
  if (!network) return null;

  return (
    <Card className="bg-card/50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Wifi className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Network</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {network.isp && (
          <div>
            <p className="text-xs text-muted-foreground">ISP</p>
            <p className="font-medium">{network.isp}</p>
          </div>
        )}
        {network.organization && network.organization !== network.isp && (
          <div>
            <p className="text-xs text-muted-foreground">Organization</p>
            <p className="font-medium">{network.organization}</p>
          </div>
        )}
        {network.asn && (
          <div>
            <p className="text-xs text-muted-foreground">ASN</p>
            <p className="font-mono text-sm">
              AS{network.asn} {network.as_name && `(${network.as_name})`}
            </p>
          </div>
        )}
        {network.connection_type && (
          <Badge variant="outline" className="text-xs capitalize">
            {network.connection_type}
          </Badge>
        )}
        {network.cloud_provider && (
          <div className="flex items-center gap-2">
            <Cloud className="h-4 w-4 text-cyan-400" />
            <span className="text-sm">
              {network.cloud_provider}
              {network.cloud_service && ` ${network.cloud_service}`}
              {network.cloud_region && ` (${network.cloud_region})`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Risk Factors List
function RiskFactorsList({ factors }: { factors?: RiskFactor[] }) {
  if (!factors || factors.length === 0) return null;

  const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  const sortedFactors = [...factors].sort(
    (a, b) => severityOrder[a.severity] - severityOrder[b.severity]
  );

  const issues: Issue[] = sortedFactors.map((factor, index) => ({
    id: `risk-${index}`,
    severity: factor.severity,
    message: factor.description,
    code: factor.category,
    module: "ipintel",
  }));

  return <IssuesList issues={issues} groupBySeverity showCounts />;
}

// Main Component
export function IpIntelModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: IpIntelModuleViewProps) {
  // Calculate inverted score for display (0 = best, 100 = worst in backend; invert for UI)
  const displayScore = data.score !== undefined ? 100 - data.score : undefined;
  const grade = getGrade(data.score);
  const isHighRisk = (data.score ?? 0) > 60;

  return (
    <ModuleContainer
      moduleName="ipintel"
      icon={Globe}
      data={data}
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Header with IP and Score */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <p className="text-2xl font-mono font-bold">{data.ip}</p>
              <Badge variant="outline" className="text-xs">
                IPv{data.ip_version || 4}
              </Badge>
              {data.cached && (
                <Badge variant="outline" className="text-xs bg-muted">
                  Cached
                </Badge>
              )}
            </div>
            {data.hostname && (
              <p className="text-sm text-muted-foreground mt-1">{data.hostname}</p>
            )}
          </div>

          {displayScore !== undefined && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">Risk Score</p>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "text-3xl font-bold font-mono",
                      isHighRisk ? "text-red-400" : "text-emerald-400"
                    )}
                  >
                    {grade}
                  </span>
                  <span className="text-sm text-muted-foreground">({data.score}/100)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Score Bar */}
        {displayScore !== undefined && (
          <ScoreBar
            score={displayScore}
            showValue={false}
          />
        )}

        {/* Main Info Grid */}
        <div className="grid gap-4 md:grid-cols-2">
          <LocationCard geolocation={data.geolocation} metadata={data.metadata} />
          <SecurityStatusCard security={data.security} />
        </div>

        {/* Network Info */}
        {data.network && (
          <ModuleSection title="Network Information" icon={Wifi}>
            <NetworkInfoCard network={data.network} />
          </ModuleSection>
        )}

        {/* Risk Factors */}
        {data.risk_factors && data.risk_factors.length > 0 && (
          <ModuleSection
            title="Risk Factors"
            icon={AlertTriangle}
            badge={
              <Badge
                variant="outline"
                className={cn(
                  "text-xs",
                  isHighRisk
                    ? "bg-red-500/10 text-red-400 border-red-500/30"
                    : "bg-amber-500/10 text-amber-400 border-amber-500/30"
                )}
              >
                {data.risk_factors.length}
              </Badge>
            }
          >
            <RiskFactorsList factors={data.risk_factors} />
          </ModuleSection>
        )}

        {/* Metadata Footer */}
        {(data.lookup_time_ms !== undefined || data.provider) && (
          <div className="flex items-center justify-end gap-4 text-xs text-muted-foreground">
            {data.lookup_time_ms !== undefined && <span>Lookup: {data.lookup_time_ms}ms</span>}
            {data.provider && <span>Provider: {data.provider}</span>}
          </div>
        )}
      </div>
    </ModuleContainer>
  );
}

IpIntelModuleView.displayName = "IpIntelModuleView";
